import { Navigate, Link } from 'react-router-dom'
import { useAuth } from '@clerk/clerk-react'

import { cn } from '@/lib/utils'

// ---------------------------------------------------------------------------
// Kanban Mockup Data
// ---------------------------------------------------------------------------

const KANBAN_COLUMNS = [
  {
    id: 'researching',
    label: 'Researching',
    color: 'bg-blue-50 border-blue-200',
    headerColor: 'text-blue-700',
    cards: ['Wharton MBA', 'MIT Sloan'],
  },
  {
    id: 'preparing',
    label: 'Preparing',
    color: 'bg-amber-50 border-amber-200',
    headerColor: 'text-amber-700',
    cards: ['HBS MBA', 'Booth MBA'],
  },
  {
    id: 'submitted',
    label: 'Submitted',
    color: 'bg-violet-50 border-violet-200',
    headerColor: 'text-violet-700',
    cards: ['Stanford GSB'],
  },
  {
    id: 'decision',
    label: 'Decision',
    color: 'bg-green-50 border-green-200',
    headerColor: 'text-green-700',
    cards: ['Kellogg MBA'],
  },
]

// ---------------------------------------------------------------------------
// Maya Chat Mockup Data
// ---------------------------------------------------------------------------

const MAYA_CHAT = [
  {
    role: 'assistant',
    text: "Hi! I'm Maya, your AI counselor. Let's start with your background — tell me about your work experience and what's driving you toward an MBA.",
  },
  {
    role: 'user',
    text: "I've spent 4 years in strategy consulting, mostly with tech clients. I want to pivot into a product leadership role.",
  },
  {
    role: 'assistant',
    text: "Consulting-to-product is a strong MBA story — admissions committees see the analytical rigor and want to understand the 'why now'. Which programs are you targeting?",
  },
  {
    role: 'user',
    text: 'Cornell Johnson, Tuck, and UCLA Anderson.',
  },
  {
    role: 'assistant',
    text: "Great shortlist. Johnson's Tech MBA track aligns particularly well with a product pivot. Before we draft, tell me about a specific project where you influenced a product decision — that's the core of your essays.",
  },
]

// ---------------------------------------------------------------------------
// Maya Capabilities
// ---------------------------------------------------------------------------

const MAYA_CAPABILITIES = [
  'Gathers your full background in a structured Phase 1 interview',
  'Drafts personalized essays for each school, grounded in your story',
  'Remembers context across your entire application cycle',
  'Helps refine tone and structure in real-time conversation',
]

// ---------------------------------------------------------------------------
// Feature Data
// ---------------------------------------------------------------------------

const FEATURES = [
  {
    icon: (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 9.75a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375m-13.5 3.01c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.184-4.183a1.14 1.14 0 01.778-.332 48.294 48.294 0 005.83-.498c1.585-.233 2.708-1.626 2.708-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" />
      </svg>
    ),
    title: 'Maya — AI Counselor',
    description:
      'Your personal AI essay counselor. Maya learns your background, helps craft compelling school-specific essays, and refines them in conversation.',
  },
  {
    icon: (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
      </svg>
    ),
    title: 'Kanban Board',
    description:
      'Drag applications across stages — Researching, Preparing, Submitted, Decision. See your entire pipeline at a glance.',
  },
  {
    icon: (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
      </svg>
    ),
    title: 'Recommender Portal',
    description:
      'Send professors a magic link. They update their status without logging in — no accounts, no friction.',
  },
  {
    icon: (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
      </svg>
    ),
    title: 'Document Management',
    description:
      'Upload essays, transcripts, and reports. Link documents to specific requirements so nothing gets lost.',
  },
]

// ---------------------------------------------------------------------------
// Pricing Data
// ---------------------------------------------------------------------------

const PRICING_PLANS = [
  {
    name: 'Free',
    price: '₹0',
    period: '',
    yearlyPrice: null,
    applications: '3 applications',
    features: ['3 application slots', 'Kanban board', 'Basic deadline tracking', 'Community support'],
    cta: 'Get started free',
    highlight: false,
    accentClass: 'border-border',
    ctaClass: 'bg-white text-gray-900 border border-gray-300 hover:bg-gray-50',
  },
  {
    name: 'Pro',
    price: '₹1,500',
    period: '/month',
    yearlyPrice: '₹10,000/year',
    applications: '8 applications',
    features: [
      '8 application slots',
      'Kanban board',
      'Recommender portal',
      'Document management',
      'Maya AI counselor',
      'Email reminders',
    ],
    cta: 'Start Pro',
    highlight: true,
    accentClass: 'border-brand-blue ring-2 ring-brand-blue',
    ctaClass: 'bg-brand-blue text-white hover:opacity-90',
  },
  {
    name: 'Max',
    price: '₹3,000',
    period: '/month',
    yearlyPrice: '₹20,000/year',
    applications: '20 applications',
    features: [
      '20 application slots',
      'Kanban board',
      'Recommender portal',
      'Document management',
      'Maya AI counselor',
      'Email reminders',
      'Priority support',
    ],
    cta: 'Start Max',
    highlight: false,
    accentClass: 'border-brand-purple ring-1 ring-brand-purple',
    ctaClass: 'bg-brand-purple text-white hover:opacity-90',
  },
]

// ---------------------------------------------------------------------------
// LandingPage
// ---------------------------------------------------------------------------

export default function LandingPage() {
  const { isLoaded, isSignedIn } = useAuth()

  if (isLoaded && isSignedIn) {
    return <Navigate to="/applications" replace />
  }

  function scrollToSignIn(e: React.MouseEvent) {
    e.preventDefault()
    document.getElementById('signin')?.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <div className="min-h-screen bg-white text-gray-900">
      {/* ------------------------------------------------------------------ */}
      {/* Navbar                                                               */}
      {/* ------------------------------------------------------------------ */}
      <header className="sticky top-0 z-40 border-b border-gray-200 bg-white/90 backdrop-blur-sm">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <span className="text-xl font-bold text-brand-blue tracking-tight">GradNomad</span>
          <a
            href="#signin"
            onClick={scrollToSignIn}
            className="rounded-md bg-brand-blue px-4 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90"
          >
            Sign In
          </a>
        </div>
      </header>

      {/* ------------------------------------------------------------------ */}
      {/* Hero                                                                 */}
      {/* ------------------------------------------------------------------ */}
      <section className="mx-auto max-w-7xl px-6 py-20 lg:py-28">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          {/* Copy */}
          <div>
            <h1 className="text-4xl font-extrabold leading-tight tracking-tight text-gray-900 sm:text-5xl lg:text-6xl">
              Your MBA applications,{' '}
              <span className="text-brand-blue">finally under control</span>
            </h1>
            <p className="mt-6 text-lg leading-relaxed text-gray-600">
              GradNomad combines a purpose-built application tracker with Maya — an AI counselor
              who learns your story and helps you write essays that actually sound like you.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a
                href="#signin"
                onClick={scrollToSignIn}
                className="inline-flex items-center rounded-lg bg-brand-blue px-6 py-3 text-base font-semibold text-white shadow-sm transition-opacity hover:opacity-90"
              >
                Get started free
              </a>
              <a
                href="#maya"
                onClick={(e) => {
                  e.preventDefault()
                  document.getElementById('maya')?.scrollIntoView({ behavior: 'smooth' })
                }}
                className="inline-flex items-center rounded-lg border border-gray-300 bg-white px-6 py-3 text-base font-semibold text-gray-700 shadow-sm transition-colors hover:bg-gray-50"
              >
                Meet Maya
              </a>
            </div>
          </div>

          {/* Kanban Mockup */}
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 shadow-lg">
            <div className="mb-3 flex items-center gap-2">
              <div className="h-3 w-3 rounded-full bg-red-400" />
              <div className="h-3 w-3 rounded-full bg-yellow-400" />
              <div className="h-3 w-3 rounded-full bg-green-400" />
              <span className="ml-2 text-xs font-medium text-gray-400">GradNomad — Applications</span>
            </div>
            <div className="grid grid-cols-4 gap-2">
              {KANBAN_COLUMNS.map((col) => (
                <div
                  key={col.id}
                  className={cn('rounded-lg border p-2', col.color)}
                >
                  <p className={cn('mb-2 text-center text-xs font-semibold uppercase tracking-wide', col.headerColor)}>
                    {col.label}
                  </p>
                  <div className="space-y-1.5">
                    {col.cards.map((card) => (
                      <div
                        key={card}
                        className="rounded-md border border-white bg-white px-2 py-2 shadow-sm"
                      >
                        <p className="text-xs font-medium text-gray-800 leading-snug">{card}</p>
                        <div className="mt-1 flex items-center gap-1">
                          <div className="h-1.5 w-1.5 rounded-full bg-brand-blue" />
                          <span className="text-[10px] text-gray-400">2 essays</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------------ */}
      {/* Maya Section                                                         */}
      {/* ------------------------------------------------------------------ */}
      <section id="maya" className="bg-gray-950 py-20">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            {/* Copy */}
            <div>
              <span className="inline-flex items-center gap-2 rounded-full bg-brand-blue/20 px-3 py-1 text-xs font-semibold text-brand-blue uppercase tracking-wider">
                AI Counselor
              </span>
              <h2 className="mt-4 text-3xl font-bold tracking-tight text-white sm:text-4xl">
                Meet Maya
              </h2>
              <p className="mt-4 text-lg leading-relaxed text-gray-400">
                Most applicants spend thousands on consultants. Maya gives you the same structured
                guidance — available whenever you need it, at a fraction of the cost.
              </p>
              <ul className="mt-8 space-y-4">
                {MAYA_CAPABILITIES.map((cap) => (
                  <li key={cap} className="flex items-start gap-3">
                    <svg
                      className="mt-0.5 h-5 w-5 flex-shrink-0 text-brand-blue"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={2.5}
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                    <span className="text-sm leading-relaxed text-gray-300">{cap}</span>
                  </li>
                ))}
              </ul>
              <a
                href="#signin"
                onClick={scrollToSignIn}
                className="mt-8 inline-flex items-center rounded-lg bg-brand-blue px-6 py-3 text-base font-semibold text-white shadow-sm transition-opacity hover:opacity-90"
              >
                Try Maya free
              </a>
            </div>

            {/* Chat Mockup */}
            <div className="rounded-xl border border-gray-800 bg-gray-900 shadow-xl">
              {/* Chat header */}
              <div className="flex items-center gap-3 border-b border-gray-800 px-4 py-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-blue text-xs font-bold text-white">
                  M
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">Maya</p>
                  <p className="text-xs text-gray-500">AI Application Counselor</p>
                </div>
                <div className="ml-auto flex h-2 w-2 rounded-full bg-green-400" />
              </div>

              {/* Chat messages */}
              <div className="space-y-3 p-4">
                {MAYA_CHAT.map((msg, i) => (
                  <div
                    key={i}
                    className={cn('flex', msg.role === 'user' ? 'justify-end' : 'justify-start')}
                  >
                    <div
                      className={cn(
                        'max-w-[85%] rounded-2xl px-3.5 py-2.5 text-xs leading-relaxed',
                        msg.role === 'user'
                          ? 'rounded-br-sm bg-brand-blue text-white'
                          : 'rounded-bl-sm bg-gray-800 text-gray-200',
                      )}
                    >
                      {msg.text}
                    </div>
                  </div>
                ))}

                {/* Typing indicator */}
                <div className="flex justify-start">
                  <div className="flex items-center gap-1 rounded-2xl rounded-bl-sm bg-gray-800 px-3.5 py-3">
                    <div className="h-1.5 w-1.5 animate-bounce rounded-full bg-gray-400 [animation-delay:-0.3s]" />
                    <div className="h-1.5 w-1.5 animate-bounce rounded-full bg-gray-400 [animation-delay:-0.15s]" />
                    <div className="h-1.5 w-1.5 animate-bounce rounded-full bg-gray-400" />
                  </div>
                </div>
              </div>

              {/* Chat input */}
              <div className="border-t border-gray-800 px-4 py-3">
                <div className="flex items-center gap-2 rounded-lg bg-gray-800 px-3 py-2">
                  <span className="flex-1 text-xs text-gray-500">Reply to Maya...</span>
                  <svg className="h-4 w-4 text-brand-blue" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M3.478 2.405a.75.75 0 00-.926.94l2.432 7.905H13.5a.75.75 0 010 1.5H4.984l-2.432 7.905a.75.75 0 00.926.94 60.519 60.519 0 0018.445-8.986.75.75 0 000-1.218A60.517 60.517 0 003.478 2.405z" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------------ */}
      {/* Features                                                             */}
      {/* ------------------------------------------------------------------ */}
      <section id="features" className="py-20">
        <div className="mx-auto max-w-7xl px-6">
          <div className="text-center">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              Everything you need to manage your MBA journey
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              Built by applicants who know the process inside out.
            </p>
          </div>
          <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {FEATURES.map((feature, i) => (
              <div
                key={feature.title}
                className={cn(
                  'rounded-xl border p-6 transition-colors hover:border-gray-300',
                  i === 0
                    ? 'border-brand-blue/30 bg-brand-blue/5'
                    : 'border-gray-200 bg-white',
                )}
              >
                <div className={cn(
                  'flex h-12 w-12 items-center justify-center rounded-lg',
                  i === 0 ? 'bg-brand-blue/10 text-brand-blue' : 'bg-gray-100 text-gray-600',
                )}>
                  {feature.icon}
                </div>
                <h3 className="mt-4 text-base font-semibold text-gray-900">{feature.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-gray-500">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------------ */}
      {/* Pricing                                                              */}
      {/* ------------------------------------------------------------------ */}
      <section id="pricing" className="bg-gray-50 py-20">
        <div className="mx-auto max-w-7xl px-6">
          <div className="text-center">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              Simple, transparent pricing
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              Start free. Upgrade when you need more.
            </p>
          </div>
          <div className="mt-16 grid gap-8 sm:grid-cols-3">
            {PRICING_PLANS.map((plan) => (
              <div
                key={plan.name}
                className={cn(
                  'relative flex flex-col rounded-xl border bg-white p-8 shadow-sm transition-shadow hover:shadow-md',
                  plan.accentClass,
                )}
              >
                {plan.highlight && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                    <span className="rounded-full bg-brand-blue px-3 py-1 text-xs font-semibold text-white">
                      Most Popular
                    </span>
                  </div>
                )}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{plan.name}</h3>
                  <div className="mt-3 flex items-baseline gap-1">
                    <span className="text-4xl font-extrabold text-gray-900">{plan.price}</span>
                    {plan.period && (
                      <span className="text-sm text-gray-500">{plan.period}</span>
                    )}
                  </div>
                  {plan.yearlyPrice && (
                    <p className="mt-1 text-xs text-gray-500">or {plan.yearlyPrice}</p>
                  )}
                  <p className="mt-2 text-sm font-medium text-gray-700">{plan.applications}</p>
                </div>
                <ul className="mt-6 flex-1 space-y-3">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-gray-600">
                      <svg
                        className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand-blue"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={2.5}
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                      </svg>
                      {f}
                    </li>
                  ))}
                </ul>
                <a
                  href="#signin"
                  onClick={scrollToSignIn}
                  className={cn(
                    'mt-8 block rounded-lg px-4 py-2.5 text-center text-sm font-semibold transition-opacity',
                    plan.ctaClass,
                  )}
                >
                  {plan.cta}
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------------ */}
      {/* Sign-in section                                                      */}
      {/* ------------------------------------------------------------------ */}
      <section id="signin" className="bg-gray-950 py-20">
        <div className="mx-auto max-w-lg px-6 text-center">
          <h2 className="text-3xl font-bold tracking-tight text-white">
            Start tracking your applications
          </h2>
          <p className="mt-3 text-base text-gray-400">
            Create your free account — no credit card required.
          </p>
          <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
            <Link
              to="/sign-in"
              className="inline-flex items-center rounded-lg bg-brand-blue px-8 py-3 text-base font-semibold text-white shadow-sm transition-opacity hover:opacity-90"
            >
              Sign in
            </Link>
            <Link
              to="/sign-up"
              className="inline-flex items-center rounded-lg border border-gray-600 bg-transparent px-8 py-3 text-base font-semibold text-white transition-colors hover:bg-gray-800"
            >
              Create account
            </Link>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------------ */}
      {/* Footer                                                               */}
      {/* ------------------------------------------------------------------ */}
      <footer className="border-t border-gray-200 bg-white py-8">
        <div className="mx-auto max-w-7xl px-6 text-center">
          <p className="text-sm text-gray-500">
            &copy; 2025 GradNomad. Built for ambitious applicants.
          </p>
        </div>
      </footer>
    </div>
  )
}
